import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './NeuralBackground.css';

const NeuralBackground = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const neuronsRef = useRef([]);
  const pulsesRef = useRef([]);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Optimized neural network configuration for better performance
    const config = {
      neuronCount: 12, // Reduced from 20 for better performance
      maxConnections: 2, // Reduced from 3
      connectionDistance: 250, // Reduced from 300
      pulseSpeed: 1,
      neuronSize: 5,
      pulseSize: 3,
    };

    // Simple Neuron class
    class Neuron {
      constructor() {
        this.x = 0; // Will be set in initNeurons
        this.y = 0; // Will be set in initNeurons
        this.size = config.neuronSize;
        this.connections = [];
        this.activity = 0;
        this.lastFired = 0;
        this.pulsePhase = Math.random() * Math.PI * 2;
      }

      update(time) {
        this.pulsePhase += 0.02; // Slower animation
        this.activity *= 0.96; // Slower decay
        
        // Less frequent random firing for better performance
        if (Math.random() < 0.003 && time - this.lastFired > 3000) {
          this.fire(time);
        }
        
        this.size = config.neuronSize + this.activity * 1.5 + Math.sin(this.pulsePhase) * 0.3;
      }

      fire(time) {
        this.activity = 1;
        this.lastFired = time;
        
        this.connections.forEach(connection => {
          const pulse = new Pulse(this, connection.neuron, time);
          pulsesRef.current.push(pulse);
        });
      }

      draw(ctx, isDark) {
        const alpha = (0.3 + this.activity * 0.15) * (isDark ? 0.8 : 0.5); // Further reduced opacity
        
        // Draw glow with more blur effect
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size * 5);
        gradient.addColorStop(0, isDark 
          ? `rgba(0, 255, 255, ${alpha * 0.2})` 
          : `rgba(0, 150, 255, ${alpha * 0.15})`);
        gradient.addColorStop(1, 'transparent');
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 5, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw neuron with reduced opacity
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = isDark 
          ? `rgba(0, 255, 255, ${alpha * 0.8})` 
          : `rgba(0, 150, 255, ${alpha * 0.6})`;
        ctx.fill();

        // Draw core when active (more subtle)
        if (this.activity > 0.5) {
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size * 0.4, 0, Math.PI * 2);
          ctx.fillStyle = isDark 
            ? `rgba(255, 255, 255, ${this.activity * 0.3})` 
            : `rgba(255, 255, 255, ${this.activity * 0.2})`;
          ctx.fill();
        }
      }
    }

    // Simple Pulse class
    class Pulse {
      constructor(fromNeuron, toNeuron, startTime) {
        this.from = fromNeuron;
        this.to = toNeuron;
        this.startTime = startTime;
        this.progress = 0;
        this.x = fromNeuron.x;
        this.y = fromNeuron.y;
        this.size = config.pulseSize;
        this.intensity = 1;
      }

      update(time) {
        const elapsed = time - this.startTime;
        this.progress = Math.min(elapsed / 2000, 1); // Slower pulse movement
        
        this.x = this.from.x + (this.to.x - this.from.x) * this.progress;
        this.y = this.from.y + (this.to.y - this.from.y) * this.progress;
        
        this.intensity = Math.sin(this.progress * Math.PI);
        
        if (this.progress >= 1) {
          this.to.fire(time);
          return false;
        }
        
        return true;
      }

      draw(ctx, isDark) {
        if (this.intensity <= 0) return;
        
        const alpha = this.intensity * (isDark ? 0.6 : 0.4); // Reduced opacity for pulses
        
        // Draw pulse glow with blur
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size * 3);
        gradient.addColorStop(0, isDark 
          ? `rgba(255, 255, 0, ${alpha * 0.5})` 
          : `rgba(255, 200, 0, ${alpha * 0.3})`);
        gradient.addColorStop(1, 'transparent');
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw pulse
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = isDark 
          ? `rgba(255, 255, 0, ${alpha})` 
          : `rgba(255, 200, 0, ${alpha})`;
        ctx.fill();
      }
    }

    // Initialize neurons
    const initNeurons = () => {
      neuronsRef.current = [];
      
      // Ensure canvas has proper dimensions
      if (canvas.width === 0 || canvas.height === 0) {
        resizeCanvas();
      }
      
      // Create a more centered distribution
      const cols = 5;
      const rows = 4;
      const marginX = canvas.width * 0.05; // Reduced margin for better coverage
      const marginY = canvas.height * 0.05; // Reduced margin for better coverage
      const cellWidth = (canvas.width - 2 * marginX) / cols;
      const cellHeight = (canvas.height - 2 * marginY) / rows;
      
      for (let i = 0; i < config.neuronCount; i++) {
        const neuron = new Neuron();
        
        if (i < cols * rows) {
          // Grid-based positioning for first neurons
          const col = i % cols;
          const row = Math.floor(i / cols);
          neuron.x = marginX + col * cellWidth + cellWidth * 0.5 + (Math.random() - 0.5) * cellWidth * 0.4;
          neuron.y = marginY + row * cellHeight + cellHeight * 0.5 + (Math.random() - 0.5) * cellHeight * 0.4;
        } else {
          // Random positioning for extra neurons, more centered
          neuron.x = canvas.width * 0.2 + Math.random() * (canvas.width * 0.6);
          neuron.y = canvas.height * 0.2 + Math.random() * (canvas.height * 0.6);
        }
        
        // Ensure neurons stay within bounds
        neuron.x = Math.max(marginX, Math.min(canvas.width - marginX, neuron.x));
        neuron.y = Math.max(marginY, Math.min(canvas.height - marginY, neuron.y));
        
        neuronsRef.current.push(neuron);
      }
      
      // Create connections after all neurons are positioned
      neuronsRef.current.forEach(neuron => {
        const nearby = neuronsRef.current
          .filter(other => other !== neuron)
          .map(other => ({
            neuron: other,
            distance: Math.sqrt(
              Math.pow(neuron.x - other.x, 2) + Math.pow(neuron.y - other.y, 2)
            )
          }))
          .filter(item => item.distance < config.connectionDistance)
          .sort((a, b) => a.distance - b.distance)
          .slice(0, config.maxConnections);
        
        neuron.connections = nearby;
      });
    };

    // Draw connections
    const drawConnections = (ctx, isDark) => {
      neuronsRef.current.forEach(neuron => {
        neuron.connections.forEach(connection => {
          const opacity = (0.15 + neuron.activity * 0.15) * (isDark ? 1 : 0.5); // Reduced connection opacity
          
          ctx.beginPath();
          ctx.moveTo(neuron.x, neuron.y);
          ctx.lineTo(connection.neuron.x, connection.neuron.y);
          ctx.strokeStyle = isDark
            ? `rgba(0, 255, 255, ${opacity})`
            : `rgba(0, 150, 255, ${opacity})`;
          ctx.lineWidth = 0.5 + neuron.activity * 0.5; // Thinner lines
          ctx.stroke();
        });
      });
    };

    // Set canvas size
    const resizeCanvas = () => {
      // Get the hero section dimensions
      const heroSection = canvas.closest('.hero');
      if (heroSection) {
        const rect = heroSection.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
        canvas.style.width = rect.width + 'px';
        canvas.style.height = rect.height + 'px';
      } else {
        // Fallback to viewport dimensions
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        canvas.style.width = window.innerWidth + 'px';
        canvas.style.height = window.innerHeight + 'px';
      }
    };

    // Animation loop
    const animate = (time) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update neurons
      neuronsRef.current.forEach(neuron => {
        neuron.update(time);
      });

      // Update pulses
      pulsesRef.current = pulsesRef.current.filter(pulse => pulse.update(time));

      // Random activity - less frequent for better performance
      if (Math.random() < 0.01) {
        const randomNeuron = neuronsRef.current[Math.floor(Math.random() * neuronsRef.current.length)];
        randomNeuron.fire(time);
      }

      // Draw connections
      drawConnections(ctx, isDarkMode);

      // Draw neurons
      neuronsRef.current.forEach(neuron => {
        neuron.draw(ctx, isDarkMode);
      });

      // Draw pulses
      pulsesRef.current.forEach(pulse => {
        pulse.draw(ctx, isDarkMode);
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    // Initialize canvas
    resizeCanvas();
    window.addEventListener('resize', () => {
      resizeCanvas();
      // Reinitialize neurons after resize with a small delay
      setTimeout(() => {
        initNeurons();
      }, 100);
    });

    // Initialize neurons with a small delay to ensure canvas is properly sized
    setTimeout(() => {
      initNeurons();
    }, 100);
    
    // Start initial activity
    setTimeout(() => {
      if (neuronsRef.current.length > 0) {
        const randomNeuron = neuronsRef.current[Math.floor(Math.random() * neuronsRef.current.length)];
        randomNeuron.fire(performance.now());
      }
    }, 1000);

    animate(performance.now());

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      className="neural-background"
      style={{
        display: 'block',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        pointerEvents: 'none'
      }}
    />
  );
};

export default NeuralBackground;